import { httpClient } from "@/services/httpClient";
import { ALTCHA_SOLUTION_HEADER } from "@/services/altchaService";
function isNotFound(e) {
    return e?.response?.status === 404;
}
export async function getPublicForm(slug) {
    try {
        const { data } = await httpClient.get(`/public/forms/${slug}`);
        const payload = data?.data;
        // CONSTRAINT: keep before the document guard below, which a redirect payload (no document) fails.
        if (payload?.status === "Archived") {
            const target = payload.redirectToSlug;
            if (typeof target !== "string" || target === "") {
                throw new Error("Public form read returned a redirect with no target slug");
            }
            if (target === slug) {
                // CONSTRAINT: a redirect to the current slug is a duplicated navigation that never remounts the view.
                throw new Error("Public form read returned a redirect to the same slug");
            }
            return { kind: "redirect", slug: target };
        }
        if (!payload || !payload.document || !payload.status) {
            throw new Error("Public form read returned no document");
        }
        if (payload.status !== "Published" && payload.status !== "Paused") {
            throw new Error("Public form read returned an unsupported status");
        }
        return { kind: "ok", value: { status: payload.status, document: payload.document } };
    }
    catch (e) {
        if (isNotFound(e)) {
            return { kind: "not-found" };
        }
        throw e;
    }
}
export async function submitDonation(slug, body, altchaSolution) {
    const { data } = await httpClient.post(`/public/forms/${slug}/submit`, body, { headers: { [ALTCHA_SOLUTION_HEADER]: altchaSolution } });
    const payload = data?.data;
    if (!payload || typeof payload.paymentLink !== "string" || payload.paymentLink === "") {
        throw new Error("Donation submit returned no payment link");
    }
    return payload;
}
const PAYMENT_OUTCOMES = ["Paid", "Pending", "Failed", "Cancelled"];
export class PaymentOutcomeContractError extends Error {
}
export class UnsafeRefError extends Error {
}
const PAYMENT_OUTCOME_TIMEOUT_MS = 8000;
// CONSTRAINT: the ref becomes a path segment, and the edge decodes `%2F` and resolves `..` before routing, so a
// ref holding `/`, `.` or `%` could read another form's outcome. Never put a ref outside this charset in the path.
const PATH_SAFE_REF = /^[A-Za-z0-9-]+$/;
function isPaymentOutcome(value) {
    return PAYMENT_OUTCOMES.some((outcome) => outcome === value);
}
export async function getPaymentOutcome(slug, ref, signal) {
    if (!PATH_SAFE_REF.test(ref)) {
        throw new UnsafeRefError("Payment outcome ref is not path-safe");
    }
    const { data } = await httpClient.get(`/public/forms/${slug}/submissions/${ref}/outcome`, { signal, timeout: PAYMENT_OUTCOME_TIMEOUT_MS });
    const outcome = data?.data?.outcome;
    if (!isPaymentOutcome(outcome)) {
        throw new PaymentOutcomeContractError("Payment outcome read returned an unknown outcome");
    }
    return outcome;
}
